import { useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { customerId$, uid$, isAuth$ } from "selectors/auth";
import { getStripeCustomerId, getUserId } from "interceptors";

const IS_WELCOME_MESSAGE_SENT = "is_crisp_welcome_message_sent";

export const useCrispUtils = () => {
  const rawUid = useSelector(uid$) || getUserId();
  const rawCustomerId = useSelector(customerId$) || getStripeCustomerId();
  const isAuth = useSelector(isAuth$);
  const isNewUser = useRef(false);
  const isAuthRef = useRef(isAuth);
  isAuthRef.current = isAuth;

  const sanitize = (val: any) =>
    typeof val === "string" && val !== "null" && val !== "undefined" && val.trim() !== "" ? val : undefined;

  const uid = sanitize(rawUid);
  const customerId = sanitize(rawCustomerId);

  const log = (...args: any[]) => console.log("[CRISP]", ...args);

  const hasWelcomedRef = useRef(localStorage.getItem(IS_WELCOME_MESSAGE_SENT) === 'true');
  const lastPageRef = useRef<string>("");

  const initializeCrispScript = () => {
    if (typeof window === "undefined") return;

    if (!(window as any).$crisp) {
      (window as any).$crisp = [];
    }

    // Enable safe mode to prevent warnings and errors
    (window as any).$crisp.push(["safe", true]);

    if (!(window as any).CRISP_WEBSITE_ID) {
      (window as any).CRISP_WEBSITE_ID = "af7a6c3d-3bd9-4194-a78c-d5e8f8645c5e";

      if (!document.getElementById("crisp-script")) {
        const script = document.createElement("script");
        script.src = "https://client.crisp.chat/l.js";
        script.async = true;
        script.id = "crisp-script";
        document.head.appendChild(script);
      }
    }
  };

  const getCurrentPage = useCallback(() => {
    const currentIsAuth = isAuthRef.current;
    if(window.location?.pathname === "/") {
      return currentIsAuth ? '/creator/statistic' : '/sign-in'
    } else return window.location?.pathname.replace(/[^a-z0-9]/gi, "_").toLowerCase();
  }, []);

  const buildSessionData = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const username = urlParams.get("uname");
    const email = urlParams.get("email");
    const campaign = urlParams.get("cc");
    const affiliateCode = urlParams.get("afmc");
    const currentPage = getCurrentPage();

    const currentIsAuth = isAuthRef.current;
    const data: [string, string][] = [];

    // Query params
    if (username) data.push(["user-first-name", username]);
    if (email) data.push(["user-email", email]);
    if (campaign) data.push(["campaign", campaign]);
    if (affiliateCode) data.push(["afmc", affiliateCode]);
    if (isNewUser.current) data.push(["new-user", "true"]);
    data.push(["is-auth", currentIsAuth.toString()]);

    // User IDs (when logged in)
    if (uid) data.push(["user-id", uid]);
    if (customerId) data.push(["stripe-customer-id", customerId]);

    // Current context
    data.push(["current-url", window.location.href]);
    data.push(["current-page", currentPage]);
    data.push(["timestamp", new Date().toISOString()]);

    return data;
  }, [uid, customerId, isAuth]);

  const updateCrispSessionData = useCallback(() => {
    if (typeof window === "undefined") return;
    const crisp = (window as any).$crisp;

    if (!crisp || typeof crisp.push !== "function") {
      log("Crisp not ready for session data update");
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const username = urlParams.get("uname");
    const email = urlParams.get("email");
    const campaign = urlParams.get("cc");

    // Send all query params as session data for backend/operators
    const sessionData = buildSessionData();
    log("Updating session data:", sessionData);
    crisp.push(["set", "session:data", [sessionData]]);

    // Also send campaign and other params as session segments if they exist
    const segments: string[] = [];
    if (username) segments.push(`user-${username}`);
    if (email) segments.push(`user-${email}`);
    if (campaign) segments.push(`campaign-${campaign}`);
    
    if (segments.length > 0) {
      crisp.push(["set", "session:segments", [segments]]);
      log("Set session segments:", segments);
    }
  }, [buildSessionData]);

  const triggerWelcomeMessage = useCallback(() => {
    if (typeof window === "undefined" || hasWelcomedRef.current) return;

    const crisp = (window as any).$crisp;
    
    if (!crisp || typeof crisp.push !== "function") {
      console.warn("[CRISP] Crisp is not ready");
      return;
    }

    // Use session:loaded event to ensure session identifier is available
    crisp.push(["on", "session:loaded", async (sessionId: string) => {
      if (hasWelcomedRef.current) {
        updateCrispSessionData();
        return; // Already welcomed
      }
      log("Session loaded with ID:", sessionId);
      
      try {
        // Fetch welcome message
        const params = new URLSearchParams(window.location.search);
        params.set("session_id", sessionId);
        const url = `${process.env.REACT_APP_AGENT_URL}/welcome-message?${params.toString()}`;

        log("Fetching welcome message from:", url.toString());

        const response = await fetch(url.toString());
        
        log("Welcome message response status:", response.status);
        log("Welcome message response headers:", Object.fromEntries(response.headers.entries()));
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          const textResponse = await response.text();
          console.warn("[CRISP] Expected JSON but got:", contentType, "Response:", textResponse.substring(0, 200));
          throw new Error(`Expected JSON response but got ${contentType}`);
        }

        const data = await response.json();
        const message = data?.welcome_message || "Welcome to our site!";
        console.log("[CRISP] Welcome message received:", message, data);
        // crisp.push(["do", "chat:open"]);
        crisp.push(["do", "message:show", ["text", message]]);
        log("Welcome message sent:", message);

        localStorage.setItem(IS_WELCOME_MESSAGE_SENT, 'true');
        hasWelcomedRef.current = true;
        updateCrispSessionData();
      } catch (err) {
        console.error("[CRISP] Failed to fetch welcome message:", err);
        
        updateCrispSessionData(); 
        hasWelcomedRef.current = true;
      }
    }]);
  }, [updateCrispSessionData]);

  const trackPageNavigation = useCallback(() => {
    const currentPage = getCurrentPage();
    
    // Only update if page actually changed
    if (currentPage !== lastPageRef.current) {
      lastPageRef.current = currentPage;
      log("Page navigation detected:", currentPage);
      updateCrispSessionData();
    }
  }, [updateCrispSessionData]);

  const waitForCrisp = () => {
    const maxTries = 20;
    let count = 0;

    const interval = setInterval(() => {
      const crisp = (window as any).$crisp;
      if (crisp?.push && typeof crisp.push === "function") {
        clearInterval(interval);
        log("Crisp initialized");
        
        // Setup welcome message listener
        triggerWelcomeMessage();
        
      } else if (++count >= maxTries) {
        clearInterval(interval);
        console.warn("[CRISP] Timed out waiting for widget");
      }
    }, 500);
  };

  // Initialize Crisp and send welcome message
  useEffect(() => {
    if (typeof window === "undefined") return;

    initializeCrispScript();
    waitForCrisp();
  }, []); // Only run once on mount

  // Update session data when user context changes (login/logout)
  useEffect(() => {
    if (hasWelcomedRef.current) {
      log("User context changed, updating session data");
      updateCrispSessionData();
    }
  }, [uid, customerId, updateCrispSessionData]);

  // Track page navigation
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Track initial page
    lastPageRef.current = getCurrentPage();

    // Listen for navigation events
    const handleNavigation = () => {
      // Small delay to ensure URL has updated
      setTimeout(() => trackPageNavigation(), 200);
    };

    // For SPAs - listen to history changes
    window.addEventListener("popstate", handleNavigation);
    
    // For modern routers that use pushState/replaceState
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function(...args) {
      originalPushState.apply(this, args);
      handleNavigation();
    };

    window.history.replaceState = function(...args) {
      originalReplaceState.apply(this, args);
      handleNavigation();
    };

    return () => {
      window.removeEventListener("popstate", handleNavigation);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [trackPageNavigation]);

  return {
    initializeCrispScript,
    isNewUser,
    updateCrispSessionData,
    triggerWelcomeMessage,
    trackPageNavigation,
  };
};